import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import './Sidebar.css';
import UserProfile from './UserProfile';
import { useUserStorage } from '../services/userDataStorage';
import { GridMetadata } from '../services/firestoreService';
import { usePlanSync } from '../hooks/usePlanSync';
import { 
  HomeIcon, 
  TemplatesIcon, 
  PricingIcon,
  UploadIcon, 
  HideSidebarIcon, 
  AddIcon,
  CloseIcon
} from './Icons';
import { usePostHog } from 'posthog-js/react';
import ConfirmDialog from './ConfirmDialog';
import ArrowOutwardOutlinedIcon from '@mui/icons-material/ArrowOutwardOutlined';
import * as Popover from '@radix-ui/react-popover';

interface SidebarProps {
  onHideSidebar?: () => void;
  onGridSelect?: (gridId: string) => void;
  activeGridId?: string;
  onDeleteGrid?: (id: string) => void;
  onSidebarRefresh?: () => void;
  refreshCounter?: number;
  onSetSubscriptionRefresh?: (refreshFn: () => void) => void; // Callback to set refresh function
}

const Sidebar: React.FC<SidebarProps> = ({ onHideSidebar, onGridSelect, activeGridId, onDeleteGrid, onSidebarRefresh, refreshCounter, onSetSubscriptionRefresh }) => {
  const userStorage = useUserStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const [grids, setGrids] = useState<GridMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredGrid, setHoveredGrid] = useState<string | null>(null);
  const [creatingSheet, setCreatingSheet] = useState(false);
  const [deletingGrid, setDeletingGrid] = useState<string | null>(null);
  const [showCreditsTooltip, setShowCreditsTooltip] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Use the new plan sync hook instead of manually managing subscription data
  const { 
    subscription: subscriptionData, 
    loading: loadingSubscription, 
    refreshSubscription,
  } = usePlanSync(true, 60 * 1000); // Auto-sync every 1 minute

  // Track if we've loaded grids for the current Firebase session
  const lastFirebaseReady = useRef<boolean>(false);
  const lastRefreshCounter = useRef<number | undefined>(undefined);

  // Load user grids when component mounts or when Firebase is ready or refreshCounter changes
  useEffect(() => {
    const loadGrids = async () => {
      if (!userStorage?.isFirebaseReady) {
        return;
      }

      // Check if we should reload:
      // 1. Firebase just became ready for the first time
      // 2. Refresh counter changed (intentional refresh)
      const firebaseJustReady = !lastFirebaseReady.current && userStorage.isFirebaseReady;
      const refreshCounterChanged = lastRefreshCounter.current !== refreshCounter;
      
      if (!firebaseJustReady && !refreshCounterChanged) {
        return; // Skip unnecessary reloads
      }

      try {
        setLoading(true);
        const userGrids = await userStorage.getAllGrids();
        setGrids(userGrids);
        
        // Update tracking refs
        lastFirebaseReady.current = userStorage.isFirebaseReady;
        lastRefreshCounter.current = refreshCounter;
        
      } catch (error) {
        console.error('Failed to load grids:', error);
      } finally {
        setLoading(false);
      }
    };

    loadGrids();
    
    // Clear hovered state when list refreshes to avoid stale hover states
    setHoveredGrid(null);
    
    // After a short delay, check if mouse is over any grid item
    setTimeout(() => {
      const mouseOverElement = document.querySelector('.spreadsheet-item:hover');
      if (mouseOverElement) {
        const hoveredId = mouseOverElement.getAttribute('data-grid-id');
        if (hoveredId) {
          setHoveredGrid(hoveredId);
        }
      }
    }, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStorage?.isFirebaseReady, refreshCounter]);

  // Set up the subscription refresh function for parent components
  useEffect(() => {
    if (onSetSubscriptionRefresh) {
      onSetSubscriptionRefresh(refreshSubscription);
    }
  }, [onSetSubscriptionRefresh, refreshSubscription]);

  const handleCreateNewGrid = async () => {
    if (!userStorage || creatingSheet) return;

    setCreatingSheet(true);
    
    try {
      const newGrid = await userStorage.createNewGrid();
      setGrids(prev => [newGrid, ...prev]);
      posthog?.capture('grid_created', { 
        grid_id: newGrid.id, 
        source: 'sidebar_button',
        total_grids: grids.length + 1 
      });
      // Trigger sidebar refresh if callback provided
      if (onSidebarRefresh) onSidebarRefresh();
      // Navigate to the new grid
      navigate(`/sheets/${newGrid.id}`);
    } catch (error) {
      console.error('Failed to create new grid:', error);
      posthog?.capture('grid_create_failed', { error: String(error) });
      toast.error('Failed to create new sheet. Please try again.');
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleGridSelect = (gridId: string) => {
    posthog?.capture('grid_selected', { 
      grid_id: gridId, 
      source: 'sidebar_list',
      current_path: location.pathname 
    });
    
    if (onGridSelect) {
      onGridSelect(gridId);
    } else {
      // If no onGridSelect handler, navigate directly
      navigate(`/sheets/${gridId}`);
    }
  };

  const handleDeleteClick = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent triggering the parent li onClick
    
    if (onDeleteGrid && userStorage && !deletingGrid) {
      setDeletingGrid(id);
      
      try {
        // Get all grids to check if this is the last one
        const allGrids = await userStorage.getAllGrids();
        const isLastGrid = allGrids.length === 1;
        
        // Check if this is the current grid
        const isCurrentlySelected = location.pathname === `/sheets/${id}`;
        
        if (isLastGrid) {
          try {
            // Delete the current grid first
            await onDeleteGrid(id);
          } catch (deleteError) {
            console.error(`Error deleting last grid ${id}:`, deleteError);
            // Continue anyway - we'll create a new grid regardless
          }
          
          // Add delay to ensure deletion is processed
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Now create new grid (always do this, even if delete failed)
          const newGrid = await userStorage.createNewGrid();
          
          // Navigate to the new grid
          navigate(`/sheets/${newGrid.id}`);
          
          // Reload grids to update sidebar
          const updatedGrids = await userStorage.getAllGrids();
          setGrids(updatedGrids);
          
          // Trigger sidebar refresh
          if (onSidebarRefresh) {
            onSidebarRefresh();
          }
          
        } else {
          // Normal deletion logic for when there are multiple grids
          
          // If deleting the current grid, find the most recent one to navigate to
          if (isCurrentlySelected) {
            // Find the most recent grid (excluding the one being deleted)
            const remainingGrids = allGrids.filter(grid => grid.id !== id);
            
            // Delete the grid first
            onDeleteGrid(id);
            
            // Add delay to ensure deletion is processed
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Immediately reload grids to update sidebar
            const updatedGrids = await userStorage.getAllGrids();
            setGrids(updatedGrids);
            
            // Navigate to most recent remaining grid
            const mostRecentGrid = remainingGrids.sort((a, b) => b.lastModified - a.lastModified)[0];
            navigate(`/sheets/${mostRecentGrid.id}`);
            
            toast.success('Grid deleted successfully');
          } else {
            // Delete the grid (not currently selected)
            onDeleteGrid(id);
            
            // Add delay to ensure deletion is processed
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Immediately reload grids to update sidebar
            const updatedGrids = await userStorage.getAllGrids();
            setGrids(updatedGrids);
            
            toast.success('Grid deleted successfully');
          }
        }
        
      } catch (error) {
        console.error('Error in delete operation:', error);
        toast.error('Failed to delete sheet. Please try again.');
        // Try to reload grids even if deletion failed
        try {
          const updatedGrids = await userStorage.getAllGrids();
          setGrids(updatedGrids);
        } catch (reloadError) {
          console.error('Failed to reload grids after delete error:', reloadError);
        }
      } finally {
        setDeletingGrid(null);
      }
    }
  };

  // Navigation handlers
  const handleHomeClick = () => {
    posthog?.capture('navigation', { 
      from: location.pathname, 
      to: '/home',
      source: 'sidebar' 
    });
    navigate('/home');
  };

  const handleTemplatesClick = () => {
    posthog?.capture('navigation', { 
      from: location.pathname, 
      to: '/templates',
      source: 'sidebar' 
    });
    navigate('/templates');
  };

  const handlePricingClick = () => {
    posthog?.capture('navigation', { 
      from: location.pathname, 
      to: '/pricing',
      source: 'sidebar' 
    });
    navigate('/pricing');
  };

  const handleImportClick = () => {
    posthog?.capture('navigation', { 
      from: location.pathname, 
      to: '/import',
      source: 'sidebar' 
    });
    navigate('/import');
  };

  // Determine active navigation item based on current route
  const getActiveNavItem = () => {
    if (location.pathname === '/home') return 'home';
    if (location.pathname === '/templates') return 'templates';
    if (location.pathname === '/pricing') return 'pricing';
    if (location.pathname === '/import') return 'import';
    if (location.pathname.startsWith('/sheets/')) return 'sheets';
    return 'home'; // default
  };

  const activeNavItem = getActiveNavItem();

  return (
    <div className="sidebar">
      <div className="sidebar-content">
        <div className="sidebar-header">
          <button className="app-logo-btn" onClick={() => navigate('/home')}>
          <div className="app-logo">
            <img src="/logo-full.png" alt="Logo" className="logo-image" />
          </div>
          </button>
          <button className="hide-sidebar-btn" onClick={onHideSidebar} title="Hide sidebar">
            <HideSidebarIcon width={20} height={20} />
          </button>
        </div>
        
        <div className="blank-sheet-container">
          <button 
            className="blank-sheet-button" 
            onClick={handleCreateNewGrid}
            disabled={creatingSheet}
          >
            <span className="blank-sheet-text">
              {creatingSheet ? 'Creating...' : 'Blank Spreadsheet'}
            </span>
            <div className="blank-sheet-icon">
              {creatingSheet ? (
                <div className="creating-spinner"></div>
              ) : (
              <AddIcon width={14} height={14} fill="white" />
              )}
            </div>
          </button>
        </div>
        
        <div className="scrollable-nav-content">
          <div className="sidebar-nav">
            <button 
              className={`nav-button ${activeNavItem === 'home' ? 'active' : ''}`}
              onClick={handleHomeClick}
            >
              <span className="nav-icon">
                <HomeIcon width={20} height={20} />
              </span>
              <span className="nav-text">Home</span>
            </button>
            <button 
              className={`nav-button ${activeNavItem === 'templates' ? 'active' : ''}`}
              onClick={handleTemplatesClick}
            >
              <span className="nav-icon">
                <TemplatesIcon width={20} height={20} />
              </span>
              <span className="nav-text">Templates</span>
            </button>
            <button 
              className={`nav-button ${activeNavItem === 'pricing' ? 'active' : ''}`}
              onClick={handlePricingClick}
            >
              <span className="nav-icon">
                <PricingIcon width={20} height={20} />
              </span>
              <span className="nav-text">Pricing</span>
            </button>
            <button 
              className={`nav-button ${activeNavItem === 'import' ? 'active' : ''}`}
              onClick={handleImportClick}
            >
              <span className="nav-icon">
                <UploadIcon width={20} height={20} />
              </span>
              <span className="nav-text">
                Import
              </span>
            </button>
          </div>
          
          <div className="spreadsheets-list">
            <div className="spreadsheet-group">
              <h3 className="group-title">Recent Grids</h3>
              {loading ? (
                <div className="loading-grids">
                  <p>Loading grids...</p>
                </div>
              ) : (
              <ul className="spreadsheet-items">
                  {grids.length === 0 ? (
                    <li className="no-grids">
                      <span>No grids yet. Create your first grid!</span>
                </li>
                  ) : (
                    grids.map((grid) => {
                      // Determine if this grid is active based on current route
                      const isActiveGrid = location.pathname === `/sheets/${grid.id}`;
                      
                      return (
                        <li 
                          key={grid.id}
                          className={`spreadsheet-item ${isActiveGrid ? 'active' : ''}`}
                          data-grid-id={grid.id}
                          onClick={() => handleGridSelect(grid.id)}
                          onMouseEnter={() => setHoveredGrid(grid.id)}
                          onMouseLeave={() => setHoveredGrid(null)}
                        >
                          <span>{grid.title}</span>
                          {hoveredGrid === grid.id && onDeleteGrid && (
                            <button 
                              className="delete-button" 
                              onClick={(e) => handleDeleteClick(e, grid.id)}
                              title="Delete grid"
                              disabled={deletingGrid === grid.id}
                            >
                              {deletingGrid === grid.id ? (
                                <div className="deleting-spinner"></div>
                              ) : (
                                <CloseIcon width={12} height={12} />
                              )}
                            </button>
                          )}
                </li>
                      );
                    })
                  )}
              </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Blocks */}
      <div className="sidebar-bottom">
        {/* Credits Block */}
        <div className="sidebar-block credits-block">
          <div className="credits-header">
            <span className="credits-title">Credits</span>
            <Popover.Root open={showCreditsTooltip}>
              <Popover.Trigger asChild>
                <span 
                  className="credits-info-icon"
                  onMouseEnter={() => setShowCreditsTooltip(true)}
                  onMouseLeave={() => setShowCreditsTooltip(false)}
                  style={{ cursor: 'pointer' }}
                >
                  ⓘ
                </span>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  className="radix-popover-content"
                  side="top"
                  align="end"
                  sideOffset={8}
                >
                  <div className="popover-tooltip">
                    Each cell enrichment costs 1 credit.
                  </div>
                  <Popover.Arrow className="radix-popover-arrow" />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>
          <div className="credits-balance">
            {loadingSubscription ? (
              <>
                <span className="credits-count">...</span>
                <span className="credits-total">/...</span>
              </>
            ) : subscriptionData ? (
              <>
                <span className="credits-count">{subscriptionData.credits.toLocaleString()}</span>
                <span className="credits-total">/{subscriptionData.maxCredits.toLocaleString()}</span>
              </>
            ) : (
              <>
                <span className="credits-count">0</span>
                <span className="credits-total">/0</span>
              </>
            )}
          </div>
          <div className="credits-progress">
            <div 
              className="credits-progress-bar" 
              style={{ 
                width: loadingSubscription 
                  ? '0%' 
                  : subscriptionData 
                    ? `${Math.min((subscriptionData.credits / subscriptionData.maxCredits) * 100, 100)}%`
                    : '0%'
              }}
            ></div>
          </div>
          <button 
            className="credits-subscribe-btn"
            onClick={() => {
              posthog?.capture('subscription_cta_clicked', { 
                source: 'sidebar_credits',
                current_plan: subscriptionData?.plan || 'unknown',
                credits_remaining: subscriptionData?.credits || 0
              });
              navigate('/pricing');
            }}
          >
            {(!subscriptionData || subscriptionData.plan === 'free_user') ? 'Subscribe' : 'Upgrade'} <ArrowOutwardOutlinedIcon sx={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Profile Block */}
        <div className="sidebar-block profile-block">
          <UserProfile />
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Continue"
        confirmVariant="primary"
      />
    </div>
  );
};

export default Sidebar; 